/**
 * Показ раздела «Оплаты» без сервера: `VITE_MOCK_API=1`.
 *
 * Выдуманные поставщики и накладные в форме ответов `portal_payments.py`.
 * Отметки живут в памяти вкладки: «Оплачено» убирает платёжку из недели, а
 * повторное нажатие отвечает «Уже отмечено» — так же, как сервер. Правил
 * сервиса здесь нет (дробление, переносы) — платёжки заданы готовыми.
 */

import { ApiError } from '@shell/api';
import type { PortalRequest } from '../../api';
import type { InvoiceCard, Order, PayInvoice, PaymentMark } from './api';

const today = new Date();
const day = (offset: number) => new Date(today.getTime() + offset * 86_400_000).toISOString().slice(0, 10);
const weekday = (iso: string) => ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'][new Date(iso).getDay()];

interface MockInvoice extends PayInvoice {
  marks: PaymentMark[];
}

let nextMark = 900;

function inv(
  id: number,
  supplier: [number, string],
  number: string,
  docOffset: number,
  deferral: number,
  total: string,
  extra: Partial<MockInvoice> = {},
): MockInvoice {
  const due = day(docOffset + deferral);
  const overdue = Math.max(0, Math.round((today.getTime() - new Date(due).getTime()) / 86_400_000));
  return {
    id,
    number,
    external_number: null,
    supplier: { id: supplier[0], name: supplier[1] },
    doc_date: day(docOffset),
    is_posted: true,
    is_active: true,
    due_date: due,
    overdue_days: overdue,
    due_override: null,
    total,
    vat: (Number(total) / 11).toFixed(2),
    paid: '0.00',
    adjusted: '0.00',
    balance: total,
    status: 'не оплачена',
    is_settled: false,
    late_posting_days: 0,
    backdated: false,
    source: 'StoreHouse 5',
    marks: [],
    ...extra,
  };
}

const MIRATORG: [number, string] = [11, 'ООО «Мираторг-Запад»'];
const NORDIKO: [number, string] = [12, 'ООО «Нордико»'];
const PRODSTAR: [number, string] = [13, 'Продстар-Торговый Дом'];
const NESKO: [number, string] = [14, 'ООО «Неско»'];

const invoices: MockInvoice[] = [
  inv(301, MIRATORG, 'МЗ-18842', -14, 10, '48210.40'),
  inv(302, MIRATORG, 'МЗ-18907', -12, 10, '21980.00'),
  inv(303, NORDIKO, 'НД-5561', -18, 14, '15320.00', { external_number: '5561' }),
  inv(304, NORDIKO, 'НД-5570', -17, 14, '9870.50'),
  inv(305, NORDIKO, 'НД-5598', -13, 14, '12400.00', { backdated: true, late_posting_days: 5 }),
  inv(306, PRODSTAR, 'ПТ-7710', -25, 14, '64200.00'),
  inv(307, NESKO, '0078-041049', -9, 21, '118900.00'),
  inv(308, PRODSTAR, 'ПТ-7788', -1, 14, '8340.00', {
    is_posted: false,
    due_date: null,
    overdue_days: 0,
    status: 'не проведена',
  }),
];

function recompute(invoice: MockInvoice) {
  const paid = invoice.marks.filter((m) => !m.cancelled).reduce((s, m) => s + Number(m.amount), 0);
  const balance = Number(invoice.total) - paid;
  invoice.paid = paid.toFixed(2);
  invoice.balance = balance.toFixed(2);
  invoice.is_settled = invoice.is_posted && balance <= 0;
  invoice.status = !invoice.is_posted
    ? 'не проведена'
    : balance <= 0
      ? 'оплачена'
      : paid > 0
        ? 'оплачена частично'
        : 'не оплачена';
}

const plain = (invoice: MockInvoice): PayInvoice => {
  const { marks: _marks, ...rest } = invoice;
  return rest;
};

const open = () => invoices.filter((i) => i.is_posted && !i.is_settled);

function orders(): { supplier_id: number; supplier_name: string; split: boolean; total: string; orders: Order[] }[] {
  const weekEnd = day(7 - (today.getDay() || 7));
  const groups = new Map<number, MockInvoice[][]>();
  for (const invoice of open()) {
    if ((invoice.due_date ?? '') > weekEnd) continue;
    const split = invoice.supplier.id === NORDIKO[0];
    const list = groups.get(invoice.supplier.id) ?? [];
    if (split && list.length && (new Date(invoice.doc_date).getTime() - new Date(list[list.length - 1][0].doc_date).getTime()) / 86_400_000 <= 2) {
      list[list.length - 1].push(invoice);
    } else if (!split && list.length) {
      list[0].push(invoice);
    } else {
      list.push([invoice]);
    }
    groups.set(invoice.supplier.id, list);
  }
  return [...groups.values()]
    .map((groupsOfSupplier) => {
      const first = groupsOfSupplier[0][0];
      const built = groupsOfSupplier.map((items) => {
        const due = items.map((i) => i.due_date!).sort()[0];
        let pay = due;
        const wd = new Date(due).getDay();
        if (wd === 6) pay = day(Math.round((new Date(due).getTime() - today.getTime()) / 86_400_000) - 1);
        if (wd === 0) pay = day(Math.round((new Date(due).getTime() - today.getTime()) / 86_400_000) - 2);
        return {
          supplier_id: first.supplier.id,
          first_id: items[0].id,
          invoice_ids: items.map((i) => i.id),
          amount: items.reduce((s, i) => s + Number(i.balance), 0).toFixed(2),
          due_date: due,
          pay_by: pay,
          pay_by_weekday: weekday(pay),
          overdue: due < day(0),
          invoices: items.map(plain),
        };
      });
      return {
        supplier_id: first.supplier.id,
        supplier_name: first.supplier.name,
        split: first.supplier.id === NORDIKO[0],
        total: built.reduce((s, o) => s + Number(o.amount), 0).toFixed(2),
        orders: built,
      };
    })
    .sort((a, b) => Number(b.total) - Number(a.total));
}

const sum = (items: PayInvoice[]) => items.reduce((s, i) => s + Number(i.balance), 0).toFixed(2);

function card(invoice: MockInvoice): InvoiceCard {
  return {
    invoice: plain(invoice),
    threshold: '50000.00',
    needs_confirmation: invoice.is_posted && !invoice.is_settled && Number(invoice.balance) > 50000,
    actions: !invoice.is_posted
      ? []
      : invoice.is_settled
        ? ['change_due']
        : ['pay_full', 'pay_partial', 'change_due'],
    payments: invoice.marks,
    adjustments: [],
  };
}

function mark(invoice: MockInvoice, amount: string) {
  invoice.marks.push({
    id: nextMark++,
    amount,
    paid_on: day(0),
    marked_by: 'Пётр Соколов',
    marked_at: new Date().toISOString(),
    comment: null,
    cancelled: false,
    cancelled_at: null,
    cancel_reason: null,
    can_cancel: true,
  });
  recompute(invoice);
}

const conflict = (text: string) => new ApiError(409, 'conflict', text);
const bad = (text: string) => new ApiError(400, 'bad request', text);

export async function mock(path: string, options: PortalRequest): Promise<unknown | undefined> {
  const [pathname, search = ''] = path.split('?');
  if (!pathname.startsWith('/api/ops/payments/')) return undefined;
  const params = new URLSearchParams(search);
  const body = (options.body ?? {}) as Record<string, unknown>;
  const synced_at = new Date(Date.now() - 12 * 60_000).toISOString();

  if (pathname === '/api/ops/payments/week') {
    // Заглушка делит по платёжкам; настоящий сервер — по накладным.
    const view = params.get('view') ?? 'all';
    const week = orders()
      .map((w) => ({
        ...w,
        orders: w.orders.filter((o) => view === 'all' || (view === 'late') === o.overdue),
      }))
      .filter((w) => w.orders.length > 0)
      .map((w) => ({ ...w, total: w.orders.reduce((s, o) => s + Number(o.amount), 0).toFixed(2) }));
    return {
      today: day(0),
      start: day(1 - (today.getDay() || 7)),
      end: day(7 - (today.getDay() || 7)),
      orders: week.reduce((s, w) => s + w.orders.length, 0),
      total: week.reduce((s, w) => s + Number(w.total), 0).toFixed(2),
      synced_at,
      suppliers: week,
    };
  }
  if (pathname === '/api/ops/payments/orders/paid') {
    const ids = body.invoice_ids as number[];
    const items = invoices.filter((i) => ids.includes(i.id));
    if (items.every((i) => i.is_settled)) {
      throw conflict(`Уже отмечено: Пётр Соколов, ${day(0).split('-').reverse().join('.')}. Второй оплаты не будет.`);
    }
    const amount = sum(items);
    items.forEach((i) => mark(i, i.balance));
    return { message: `Отмечено: ${items.length} из ${items.length} накладных на ${amount} ₽.`, paid: items.length, of: items.length, total: amount, problems: [] };
  }
  if (pathname === '/api/ops/payments/invoices/paid') {
    const ids = body.invoice_ids as number[];
    const items = invoices.filter((i) => ids.includes(i.id) && !i.is_settled);
    if (items.length === 0) throw conflict('Уже отмечено: долга по этим накладным нет.');
    const amount = sum(items);
    items.forEach((i) => mark(i, i.balance));
    return { message: `Отмечено: ${items.length} из ${ids.length} накладных на ${amount} ₽.`, paid: items.length, of: ids.length, total: amount, problems: [] };
  }
  if (pathname === '/api/ops/payments/debts') {
    const unsettled = open();
    const overdue = unsettled.filter((i) => i.overdue_days > 0);
    const unposted = invoices.filter((i) => !i.is_posted);
    const suppliers = new Map<number, MockInvoice[]>();
    unsettled.forEach((i) => suppliers.set(i.supplier.id, [...(suppliers.get(i.supplier.id) ?? []), i]));
    return {
      today: day(0),
      synced_at,
      threshold: '50000.00',
      overdue: { count: overdue.length, total: sum(overdue) },
      unposted: { count: unposted.length, total: sum(unposted) },
      debt: { count: unsettled.length, total: sum(unsettled) },
      suppliers: [...suppliers.values()]
        .map((items) => ({
          id: items[0].supplier.id,
          name: items[0].supplier.name,
          count: items.length,
          total: sum(items),
          overdue: items.filter((i) => i.overdue_days > 0).length,
        }))
        .sort((a, b) => Number(b.total) - Number(a.total)),
    };
  }
  if (pathname === '/api/ops/payments/invoices') {
    const kind = params.get('list');
    let title = 'Просрочено';
    let items: MockInvoice[] = open().filter((i) => i.overdue_days > 0);
    if (kind === 'unposted') {
      title = 'Не проведены';
      items = invoices.filter((i) => !i.is_posted);
    } else if (kind === 'supplier') {
      items = open().filter((i) => i.supplier.id === Number(params.get('supplier_id')));
      title = items[0]?.supplier.name ?? 'Поставщик';
    } else if (kind === 'search') {
      const q = (params.get('q') ?? '').trim().toLowerCase();
      if (q.length < 2) throw bad('Для поиска нужно хотя бы два символа.');
      title = 'Поиск';
      items = invoices.filter((i) => `${i.number} ${i.external_number ?? ''}`.toLowerCase().includes(q));
    }
    return { title, today: day(0), synced_at, count: items.length, total: sum(items), invoices: items.map(plain) };
  }
  const cardMatch = /^\/api\/ops\/payments\/invoices\/(\d+)(?:\/(paid|due-date))?$/.exec(pathname);
  if (cardMatch) {
    const invoice = invoices.find((i) => i.id === Number(cardMatch[1]));
    if (!invoice) throw new ApiError(404, 'not found', 'Накладная не найдена.');
    if (cardMatch[2] === 'paid') {
      if (invoice.is_settled) throw conflict('Уже отмечено: Пётр Соколов. Второй оплаты не будет.');
      const raw = String(body.amount ?? '').replace(/\s/g, '').replace(',', '.');
      const amount = raw ? Number(raw) : Number(invoice.balance);
      if (!Number.isFinite(amount) || amount <= 0) throw bad('Это не похоже на сумму. Напишите, например: 24800.50');
      if (!raw && amount > 50000 && !body.confirmed) throw bad(`Сумма ${amount} ₽ выше порога. Подтвердите отметку.`);
      mark(invoice, amount.toFixed(2));
      return { message: `Отмечено: ${amount.toFixed(2)} ₽.`, card: card(invoice) };
    }
    if (cardMatch[2] === 'due-date') {
      invoice.due_date = String(body.due_date);
      invoice.due_override = { date: String(body.due_date), reason: String(body.reason) };
      invoice.overdue_days = Math.max(0, Math.round((today.getTime() - new Date(invoice.due_date).getTime()) / 86_400_000));
      return { message: 'Срок изменён.', card: card(invoice) };
    }
    return card(invoice);
  }
  const cancelMatch = /^\/api\/ops\/payments\/marks\/(\d+)\/cancel$/.exec(pathname);
  if (cancelMatch) {
    const invoice = invoices.find((i) => i.marks.some((m) => m.id === Number(cancelMatch[1])));
    const payment = invoice?.marks.find((m) => m.id === Number(cancelMatch[1]));
    if (!invoice || !payment) throw new ApiError(404, 'not found', 'Отметка не найдена.');
    if (payment.cancelled) throw conflict('Эта отметка уже отменена.');
    Object.assign(payment, { cancelled: true, cancelled_at: new Date().toISOString(), cancel_reason: String(body.reason), can_cancel: false });
    recompute(invoice);
    return { message: 'Отметка отменена, остаток восстановлен.', card: card(invoice) };
  }
  if (pathname === '/api/ops/payments/reports') {
    return {
      reports: [
        { code: 'P2', title: 'Просроченные', needs_period: false, supplier: false, min_total: false },
        { code: 'P3', title: 'Долг поставщику', needs_period: false, supplier: true, min_total: false },
        { code: 'P4', title: 'Оплачено за период', needs_period: true, supplier: true, min_total: false },
        { code: 'P5', title: 'Накладные за период', needs_period: true, supplier: true, min_total: false },
        { code: 'P6', title: 'Крупные накладные за период', needs_period: true, supplier: false, min_total: true },
        { code: 'P7', title: 'Занесено задним числом и исчезло', needs_period: true, supplier: true, min_total: false },
      ],
      suppliers: [MIRATORG, NORDIKO, PRODSTAR, NESKO].map(([id, name]) => ({ id, name })),
      default_days: 7,
    };
  }
  const reportMatch = /^\/api\/ops\/payments\/reports\/(P\d)$/.exec(pathname);
  if (reportMatch) {
    const overdue = open().filter((i) => i.overdue_days > 0);
    const text =
      reportMatch[1] === 'P2'
        ? [
            'Просрочено',
            `${overdue.length} накладных — ${sum(overdue)} ₽`,
            '',
            ...overdue.map((i) => `• ${i.supplier.name} · ${i.balance} ₽ · просрочено ${i.overdue_days} дн.\n  накл. ${i.number} от ${i.doc_date}`),
          ].join('\n')
        : `Отчёт ${reportMatch[1]} за выбранный период.\n\nВ показе без сервера текст отчёта условный — настоящий собирает сервис ops.`;
    const needsPeriod = !['P2', 'P3'].includes(reportMatch[1]);
    return {
      code: reportMatch[1],
      title: reportMatch[1] === 'P2' ? 'Просроченные' : `Отчёт ${reportMatch[1]}`,
      period: needsPeriod ? { from: params.get('from') ?? day(-6), to: params.get('to') ?? day(0) } : null,
      text: `${text}\n\nДанные от ${new Date().toLocaleString('ru-RU')}.`,
      synced_at,
    };
  }
  return undefined;
}
